import { useState, useEffect, useRef, useCallback } from 'react';

interface SymptomItem {
  id: number;
  icon: string;
  title: string;
  description: string;
}

const symptoms: SymptomItem[] = [
  {
    id: 1,
    icon: 'ri-flashlight-line',
    title: 'Cansaço que não passa',
    description: 'Tireoide, cortisol ou metabolismo',
  },
  {
    id: 2,
    icon: 'ri-scales-line',
    title: 'Peso que não cai',
    description: 'Metabolismo e resistência à insulina',
  },
  {
    id: 3,
    icon: 'ri-heart-3-line',
    title: 'Libido lá embaixo',
    description: 'Queda dos hormônios sexuais',
  },
  {
    id: 4,
    icon: 'ri-brain-line',
    title: 'Névoa mental, sem foco',
    description: 'Oscilações hormonais no cérebro',
  },
  {
    id: 5,
    icon: 'ri-moon-line',
    title: 'Sono ruim, sem descanso',
    description: 'Cortisol e melatonina fora de hora',
  },
  {
    id: 6,
    icon: 'ri-emotion-unhappy-line',
    title: 'Irritabilidade e humor',
    description: 'Equilíbrio hormonal pedindo atenção',
  },
  {
    id: 7,
    icon: 'ri-sun-line',
    title: 'Cabelo e pele sem vida',
    description: 'Tireoide e hormônios em baixa',
  },
  {
    id: 8,
    icon: 'ri-footprint-line',
    title: 'Inchaço e dor nas pernas',
    description: 'Lipedema e retenção de líquidos',
  },
];

export default function RaioXMetabolico() {
  const [revealedIds, setRevealedIds] = useState<Set<number>>(new Set());
  const [inView, setInView] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setInView(true);
      },
      { threshold: 0.08 },
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleSymptomClick = useCallback((id: number) => {
    setRevealedIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, []);

  const revealedCount = revealedIds.size;
  const totalCount = symptoms.length;
  const allRevealed = revealedCount === totalCount;

  // Build WhatsApp message with selected symptoms
  const buildWhatsAppLink = useCallback(() => {
    const selectedSymptoms = symptoms
      .filter((s) => revealedIds.has(s.id))
      .map((s) => `- ${s.title}`)
      .join('\n');
    const text = `Olá! Gostaria de agendar uma consulta com a Dra. Melissa Ciríaco.\n\nIdentifiquei os seguintes sinais no Raio-X Metabólico:\n${selectedSymptoms || 'Nenhum sinal selecionado'}`;
    return `https://wa.me/5585999372109?text=${encodeURIComponent(text)}`;
  }, [revealedIds]);

  // Circular progress calculations
  const radius = 58;
  const circumference = 2 * Math.PI * radius;
  const progressOffset =
    circumference - (revealedCount / totalCount) * circumference;

  return (
    <section
      ref={sectionRef}
      id="raio-x"
      className="relative w-full py-20 md:py-28 overflow-hidden"
      style={{ backgroundColor: '#0E1412' }}
    >
      {/* Subtle radial glow */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse 70% 50% at 50% 35%, rgba(66,85,74,0.15) 0%, transparent 70%)',
        }}
      />

      <div className="relative z-10 max-w-[1280px] mx-auto px-6 md:px-12 lg:px-16">
        {/* Section label */}
        <div
          className="flex items-center justify-center gap-4 mb-6"
          style={{
            opacity: inView ? 1 : 0,
            transform: inView ? 'translateY(0)' : 'translateY(12px)',
            transition: 'all 0.7s ease-out 0ms',
          }}
        >
          <span
            className="h-[1px] w-8 md:w-12"
            style={{ backgroundColor: 'rgba(200, 169, 106, 0.3)' }}
          />
          <p
            className="text-[11px] tracking-[0.2em]"
            style={{
              fontFamily: 'var(--font-body)',
              color: 'rgba(200, 169, 106, 0.7)',
              fontWeight: 500,
              textTransform: 'uppercase',
            }}
          >
            Raio-X Metabólico
          </p>
          <span
            className="h-[1px] w-8 md:w-12"
            style={{ backgroundColor: 'rgba(200, 169, 106, 0.3)' }}
          />
        </div>

        {/* Headline */}
        <div
          className="text-center mb-5"
          style={{
            opacity: inView ? 1 : 0,
            transform: inView ? 'translateY(0)' : 'translateY(16px)',
            transition: 'all 0.8s ease-out 120ms',
          }}
        >
          <h2
            className="text-3xl md:text-4xl lg:text-[2.6rem]"
            style={{
              fontFamily: 'var(--font-heading)',
              color: '#F8F7F5',
              fontWeight: 400,
              lineHeight: 1.15,
            }}
          >
            Emagrecimento que não avança, cansaço que não passa
            <br />
            e libido lá embaixo{' '}
            <em
              style={{
                color: '#C8A96A',
                fontStyle: 'italic',
              }}
            >
              não são
            </em>{' '}
            “falta de disciplina”.
          </h2>
        </div>

        {/* Subheadline */}
        <div
          className="text-center mb-14 md:mb-18"
          style={{
            opacity: inView ? 1 : 0,
            transform: inView ? 'translateY(0)' : 'translateY(12px)',
            transition: 'all 0.7s ease-out 240ms',
          }}
        >
          <p
            className="text-sm md:text-base max-w-[520px] mx-auto"
            style={{
              fontFamily: 'var(--font-body)',
              color: 'rgba(248, 247, 245, 0.5)',
              fontWeight: 300,
              lineHeight: 1.7,
            }}
          >
            Quase sempre são hormônios e metabolismo que ninguém investigou.
            <br />
            <strong
              className="font-medium"
              style={{ color: 'rgba(248, 247, 245, 0.75)' }}
            >
              Acenda a luz e revele os sinais que você sente:
            </strong>
          </p>
        </div>

        {/* Two-column layout */}
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
          {/* Left: Symptoms grid */}
          <div className="flex-1">
            <div className="grid grid-cols-2 gap-3 md:gap-4">
              {symptoms.map((symptom, index) => {
                const isRevealed = revealedIds.has(symptom.id);
                return (
                  <button
                    key={symptom.id}
                    onClick={() => handleSymptomClick(symptom.id)}
                    className="relative text-left rounded-[16px] p-4 md:p-5 transition-all duration-500 cursor-pointer"
                    style={{
                      backgroundColor: isRevealed
                        ? 'rgba(200, 169, 106, 0.08)'
                        : 'rgba(255, 255, 255, 0.03)',
                      border: `1px solid ${
                        isRevealed
                          ? 'rgba(200, 169, 106, 0.25)'
                          : 'rgba(255, 255, 255, 0.06)'
                      }`,
                      opacity: inView ? 1 : 0,
                      transform: inView
                        ? 'translateY(0)'
                        : 'translateY(20px)',
                      transition: `all 0.6s ease-out ${300 + index * 80}ms, background-color 0.5s ease, border-color 0.5s ease`,
                    }}
                  >
                    {/* Icon */}
                    <div className="mb-3">
                      <div
                        className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full"
                        style={{
                          backgroundColor: isRevealed
                            ? 'rgba(200, 169, 106, 0.15)'
                            : 'rgba(255, 255, 255, 0.05)',
                          transition: 'all 0.5s ease',
                        }}
                      >
                        <i
                          className={`${symptom.icon} text-base md:text-lg`}
                          style={{
                            color: isRevealed ? '#C8A96A' : 'rgba(248, 247, 245, 0.4)',
                            transition: 'color 0.5s ease',
                          }}
                        />
                      </div>
                    </div>

                    {/* Title */}
                    <h4
                      className="text-sm md:text-base mb-1"
                      style={{
                        fontFamily: 'var(--font-heading)',
                        color: isRevealed ? '#C8A96A' : 'rgba(248, 247, 245, 0.8)',
                        fontWeight: 400,
                        transition: 'color 0.5s ease',
                      }}
                    >
                      {symptom.title}
                    </h4>

                    {/* Description */}
                    <p
                      className="text-[11px] md:text-xs leading-relaxed"
                      style={{
                        fontFamily: 'var(--font-body)',
                        color: isRevealed
                          ? 'rgba(248, 247, 245, 0.55)'
                          : 'rgba(248, 247, 245, 0.25)',
                        fontWeight: 300,
                        transition: 'color 0.5s ease',
                      }}
                    >
                      {symptom.description}
                    </p>

                    {/* Glow effect when revealed */}
                    <div
                      className="absolute inset-0 rounded-[16px] pointer-events-none transition-opacity duration-500"
                      style={{
                        boxShadow: isRevealed
                          ? '0 0 40px rgba(200, 169, 106, 0.06)'
                          : 'none',
                        opacity: isRevealed ? 1 : 0,
                      }}
                    />
                  </button>
                );
              })}
            </div>
          </div>

          {/* Right: Counter panel */}
          <div
            className="lg:w-[340px] xl:w-[380px] flex-shrink-0"
            style={{
              opacity: inView ? 1 : 0,
              transform: inView ? 'translateY(0)' : 'translateY(24px)',
              transition: 'all 0.8s ease-out 500ms',
            }}
          >
            <div
              className="rounded-[20px] p-8 md:p-10 flex flex-col items-center text-center h-full justify-center"
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid rgba(200, 169, 106, 0.12)',
              }}
            >
              {/* Circular counter */}
              <div className="relative mb-8">
                <svg
                  width="140"
                  height="140"
                  viewBox="0 0 140 140"
                  className="transform -rotate-90"
                >
                  {/* Background circle */}
                  <circle
                    cx="70"
                    cy="70"
                    r={radius}
                    fill="none"
                    stroke="rgba(255,255,255,0.06)"
                    strokeWidth="2"
                  />
                  {/* Progress circle */}
                  <circle
                    cx="70"
                    cy="70"
                    r={radius}
                    fill="none"
                    stroke="#C8A96A"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeDasharray={circumference}
                    strokeDashoffset={progressOffset}
                    style={{
                      transition: 'stroke-dashoffset 0.8s ease-out',
                    }}
                  />
                </svg>

                {/* Center text */}
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span
                    className="text-3xl md:text-4xl"
                    style={{
                      fontFamily: 'var(--font-heading)',
                      color: '#C8A96A',
                      fontWeight: 400,
                    }}
                  >
                    {revealedCount}
                  </span>
                  <span
                    className="text-[10px] tracking-[0.15em] mt-1"
                    style={{
                      fontFamily: 'var(--font-body)',
                      color: 'rgba(248, 247, 245, 0.35)',
                      fontWeight: 500,
                      textTransform: 'uppercase',
                    }}
                  >
                    De {totalCount} sinais
                  </span>
                </div>
              </div>

              {/* Instruction text */}
              <p
                className="text-base md:text-lg mb-6 max-w-[280px]"
                style={{
                  fontFamily: 'var(--font-heading)',
                  color: 'rgba(248, 247, 245, 0.6)',
                  fontWeight: 400,
                  lineHeight: 1.5,
                  fontStyle: 'italic',
                }}
              >
                Toque nos sinais — e revele o que o seu corpo esconde.
              </p>

              {/* CTA Button */}
              <a
                href={buildWhatsAppLink()}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-xs font-semibold tracking-[0.08em] uppercase whitespace-nowrap cursor-pointer transition-all duration-300 hover:shadow-[0_0_30px_rgba(200,169,106,0.25)]"
                style={{
                  fontFamily: 'var(--font-body)',
                  backgroundColor: '#C8A96A',
                  color: '#1A2420',
                }}
              >
                Fazer meu check metabólico
                <i className="ri-arrow-right-line" />
              </a>

              {/* Completion message */}
              {allRevealed && (
                <div
                  className="mt-6"
                  style={{
                    opacity: allRevealed ? 1 : 0,
                    transform: allRevealed
                      ? 'translateY(0)'
                      : 'translateY(10px)',
                    transition: 'all 0.6s ease-out',
                  }}
                >
                  <p
                    className="text-sm"
                    style={{
                      fontFamily: 'var(--font-body)',
                      color: 'rgba(200, 169, 106, 0.8)',
                      fontWeight: 400,
                    }}
                  >
                    Todos os sinais revelados. Seu corpo está pedindo atenção.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}